// src/profile/useFollowState.ts
//
// Realtime "what's the follow relationship between ME and TARGET?"
//
// State machine:
//   self          → viewer is the target (no follow button)
//   following     → /users/{target}/followers/{me} exists
//   requested     → /users/{target}/follow_requests/{me} exists
//   not_following → neither exists
//   unknown       → still loading
//
// We listen to BOTH docs so transitions (request approved, follow
// removed) reflect immediately. Cheap — they're just doc-existence
// listeners on small docs.

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import { FollowState } from './types';

export function useFollowState(targetUid: string | null): FollowState {
  const [state, setState] = useState<FollowState>('unknown');

  useEffect(() => {
    const me = auth.currentUser;
    if (!me || !targetUid) {
      setState('unknown');
      return;
    }
    if (me.uid === targetUid) {
      setState('self');
      return;
    }

    let isFollowing = false;
    let isRequested = false;
    let followerSeen = false;
    let requestSeen = false;

    const recompute = () => {
      // Wait until both listeners have fired at least once before
      // committing to "not_following" — otherwise we'd flicker
      // not_following→following on initial load.
      if (!followerSeen || !requestSeen) {
        return;
      }
      if (isFollowing) setState('following');
      else if (isRequested) setState('requested');
      else setState('not_following');
    };

    const unsubFollower = onSnapshot(
      doc(db, 'users', targetUid, 'followers', me.uid),
      (snap) => {
        isFollowing = snap.exists();
        followerSeen = true;
        recompute();
      },
      (err) => {
        console.warn('useFollowState follower listener error:', err);
        followerSeen = true;
        recompute();
      }
    );

    const unsubRequest = onSnapshot(
      doc(db, 'users', targetUid, 'follow_requests', me.uid),
      (snap) => {
        isRequested = snap.exists();
        requestSeen = true;
        recompute();
      },
      (err) => {
        console.warn('useFollowState request listener error:', err);
        requestSeen = true;
        recompute();
      }
    );

    return () => {
      unsubFollower();
      unsubRequest();
    };
  }, [targetUid]);

  return state;
}
